import { create } from "zustand";
import { persist } from "zustand/middleware";

import { lockPageScroll, unlockPageScroll } from "@/utils";

export type CareplanCartItem = {
  id: string;
  name: string;
  shortName: string;
  price: number;
  priceAsString: string;
  originalPriceAsString: string;
  unitPrice: number;
  unitPriceAsString: string;
  unitListPrice: number;
  unitListPriceAsString: string;
  image: string;
  quantity: number;
  variantId?: string;
  merchandiseId: string;
  sellingPlanId?: number | null;
  sku?: string | null;
};

export type CartState = {
  recommendedCartLines: CareplanCartItem[] | null;
  setRecommendedCartLines: (recommendedCartLines: CareplanCartItem[] | null) => void;
  showCart: boolean;
  cartCreationFailed: boolean;
  setCartCreationFailed: (failed: boolean) => void;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
  subscriptionProcessingDeadline: number;
  setSubscriptionProcessingDeadline: (deadline: number) => void;
};

export const isCartProcessing = (state: CartState) => {
  return state.subscriptionProcessingDeadline > Date.now();
};

export const partializeCartStore = (state: CartState) => {
  return { recommendedCartLines: state.recommendedCartLines };
};

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => {
      return {
        recommendedCartLines: null,
        setRecommendedCartLines: (recommendedCartLines: CareplanCartItem[] | null) => {
          set(() => {
            return { recommendedCartLines };
          });
        },
        showCart: false,
        cartCreationFailed: false,
        setCartCreationFailed: (failed: boolean) => {
          if (failed && get().showCart) {
            unlockPageScroll();
          }

          set(
            failed ? { cartCreationFailed: true, showCart: false } : { cartCreationFailed: false },
          );
        },
        openCart: () => {
          if (get().cartCreationFailed) {
            return;
          }

          lockPageScroll();
          set({ showCart: true });
        },
        closeCart: () => {
          unlockPageScroll();
          set({ showCart: false });
        },
        toggleCart: () => {
          const { cartCreationFailed, showCart } = get();

          if (cartCreationFailed && !showCart) {
            return;
          }

          if (showCart) {
            unlockPageScroll();
          } else {
            lockPageScroll();
          }
          set({ showCart: !showCart });
        },
        subscriptionProcessingDeadline: 0,
        setSubscriptionProcessingDeadline: (deadline: number) => {
          const normalizedDeadline = Number.isFinite(deadline) && deadline >= 0 ? deadline : 0;

          set({ subscriptionProcessingDeadline: normalizedDeadline });
        },
      };
    },
    {
      name: "cartStore",
      partialize: partializeCartStore,
    },
  ),
);

export const getCartStoreState = () => {
  return useCartStore.getState();
};
